import { useRef, useState } from "react";
import { format } from "date-fns";
import { Camera, Loader2, Lock, RotateCcw, Send, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Logo } from "@/components/Logo";
import { publicApi, errMsg, getPasscode } from "@/lib/api";

const isIphone = () => /iPhone/i.test(navigator.userAgent);
const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#F8FAFC] px-5 py-8" data-testid={testId}>
    <div className="mx-auto max-w-md space-y-6"><Logo />{children}</div>
  </div>
);

const Unlock = ({ onOk }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await publicApi.post("/staff/verify", { passcode: code }); sessionStorage.setItem("vp_passcode", code); sessionStorage.setItem("vp_role", data.role); onOk(); }
    catch (err) { toast.error(errMsg(err, "Incorrect passcode")); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-sm" data-testid="record-unlock-form">
      <Lock className="h-6 w-6 text-[#1D64B4]" />
      <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">Record a devotional</h1>
      <p className="mt-1 text-sm text-slate-500">Enter the staff passcode to begin.</p>
      <Input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Staff passcode" className="mt-5" data-testid="record-passcode-input" />
      <Button type="submit" disabled={!code || busy} className="mt-3 w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="record-unlock-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Continue</Button>
    </form>
  );
};

const checkClip = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => resolve({ seconds: v.duration, portrait: v.videoHeight > v.videoWidth });
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(file);
});

const Recorder = ({ onLocked }) => {
  const [f, setF] = useState({ title: "", speaker: localStorage.getItem("vp_rec_speaker") || "", devotional_date: format(new Date(), "yyyy-MM-dd") });
  const [clip, setClip] = useState(null);
  const [progress, setProgress] = useState(-1);
  const [done, setDone] = useState(false);
  const front = useRef(null);
  const back = useRef(null);
  const picked = async (file) => {
    if (!file) return;
    const m = await checkClip(file);
    if (m && m.seconds > 120.5) return toast.error(`That was ${Math.floor(m.seconds / 60)}:${String(Math.round(m.seconds % 60)).padStart(2, "0")}. Please retake it in 2 minutes or less.`);
    if (m && !m.portrait) return toast.error("Please hold your iPhone upright (portrait) and record again.");
    setClip({ file, url: URL.createObjectURL(file), seconds: m?.seconds || 0 });
  };
  const send = async () => {
    if (!f.title.trim() || !f.speaker.trim()) return toast.error("Please add a title and your name");
    localStorage.setItem("vp_rec_speaker", f.speaker);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("source", "iphone_web");
    fd.append("file", clip.file, clip.file.name || "devotional.mov");
    setProgress(0);
    try { await publicApi.post("/devotionals", fd, { headers: { "X-Staff-Passcode": getPasscode() }, onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) }); setDone(true); }
    catch (e) {
      setProgress(-1);
      if (e?.response?.status === 401) { sessionStorage.removeItem("vp_passcode"); toast.error("The passcode has changed. Please enter it again, then tap Send."); return onLocked(); }
      toast.error(errMsg(e, "Upload failed. Please check your signal and try again."));
    }
  };
  if (done) return (
    <div className="rounded-3xl bg-white p-6 text-center shadow-sm" data-testid="record-done">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
      <h2 className="mt-3 text-xl font-bold text-slate-900">Sent! Thank you.</h2>
      <p className="mt-1 text-sm text-slate-500">Your devotional is waiting for admin approval.</p>
      <Button onClick={() => { setDone(false); setClip(null); setProgress(-1); setF({ ...f, title: "" }); }} variant="outline" className="mt-5 rounded-full" data-testid="record-another-button">Record another</Button>
    </div>
  );
  return (
    <div className="space-y-4 rounded-3xl bg-white p-6 shadow-sm" data-testid="record-form">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Today's devotional</h1>
      <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" data-testid="record-title-input" />
      <Input value={f.speaker} onChange={(e) => setF({ ...f, speaker: e.target.value })} placeholder="Your name" data-testid="record-speaker-input" />
      <Input type="date" value={f.devotional_date} onChange={(e) => setF({ ...f, devotional_date: e.target.value })} data-testid="record-date-input" />
      {!clip ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Hold your iPhone upright · 2 minutes max</p>
          <input ref={front} type="file" accept="video/*" capture="user" className="hidden" onChange={(e) => { picked(e.target.files[0]); e.target.value = ""; }} data-testid="record-front-input" />
          <input ref={back} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => { picked(e.target.files[0]); e.target.value = ""; }} data-testid="record-back-input" />
          <Button onClick={() => front.current.click()} className="h-14 w-full rounded-2xl bg-[#0A3A6B] text-base hover:bg-[#1D64B4]" data-testid="record-front-button"><Camera className="mr-2 h-5 w-5" /> Record (front camera)</Button>
          <Button onClick={() => back.current.click()} variant="outline" className="h-12 w-full rounded-2xl" data-testid="record-back-button">Record with back camera</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <video src={clip.url} controls playsInline className="mx-auto aspect-[9/16] max-h-[48vh] rounded-2xl bg-black object-cover" data-testid="record-preview" />
          {progress >= 0 ? (
            <div data-testid="record-progress"><Progress value={progress} /><p className="mt-1 text-center text-xs text-slate-500">{progress >= 100 ? "Optimizing your video… almost done" : `Uploading… ${progress}%`}</p></div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setClip(null)} className="rounded-full" data-testid="record-retake-button"><RotateCcw className="mr-1 h-4 w-4" /> Retake</Button>
              <Button onClick={send} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="record-send-button"><Send className="mr-1 h-4 w-4" /> Send</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function RecordDevotional() {
  const [unlocked, setUnlocked] = useState(!!getPasscode());
  if (!isIphone()) return (
    <Shell testId="record-not-iphone">
      <div className="rounded-3xl bg-white p-6 shadow-sm"><Smartphone className="h-8 w-8 text-[#1D64B4]" />
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900">Please open this on your iPhone</h1>
        <p className="mt-2 text-sm text-slate-500">Devotional recording works on iPhone only. Scan the QR code from the Staff Hub with your iPhone camera.</p></div>
    </Shell>
  );
  return <Shell testId="record-page"><div className={unlocked ? "" : "hidden"}><Recorder onLocked={() => setUnlocked(false)} /></div>{!unlocked && <Unlock onOk={() => setUnlocked(true)} />}</Shell>;
}
